/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import groupBy from "lodash/groupBy";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { type VideojsAudioTrack } from "video.js";
import eme from "videojs-contrib-eme";

// transpiling issue with video.js, !syntax with disabled webpack loader required
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
import videojs from "!video.js";

import { ClientSideAdvertisementVideoJS, loadIMALib } from "./Advertisement";
import {
  CastSender,
  ChromecastPlayerState,
  PlaybackState,
} from "./ChromecastSender";
import { FairPlayLoader } from "./DRM";
import { getErrorMessageByStatusCode } from "./error";
import { PlayerEvent } from "./events";
import { isFairPlaySource } from "./guards";
import {
  AirPlayAvailability,
  AirPlayAvailabilityChangeEvent,
  AirPlayEvent,
  ControllerOptions,
  CurrentMediaState,
  PlayerAudioTrack,
  PlayerController,
  PlayerSource,
  PlayerSubtitleTrack,
  PlayerVideoTrack,
} from "./types";

const registerPlugin = videojs.registerPlugin || videojs.plugin;

// Supports Encrypted Media Extensions for playback of encrypted content in video.js
registerPlugin("eme", eme);

import { getPlayerRuntimeConfig } from "./playerConfig";

function buildPlayerOptions(): videojs.PlayerOptions {
  const config = getPlayerRuntimeConfig();
  const tuning = config.liveTuning;
  const vjsTuning = tuning?.videojs;

  const opts: videojs.PlayerOptions = {
    playsinline: true,
    controls: false,
    autoplay: false,
    preload: "auto",
    aspectRatio: "16:9",
    html5: {
      nativeTextTracks: false,
      vhs: {
        overrideNative: !videojs.browser.IS_IOS,
      },
      hls: {
        overrideNative: !videojs.browser.IS_IOS,
      },
    },
  };

  if (config.withCredentials) {
    opts.html5.vhs.withCredentials = true;
  }

  const vhsOptions = opts.html5?.vhs as Record<string, unknown>;
  if (tuning?.enabled && vhsOptions) {
    vhsOptions.enableLowInitialPlaylist =
      vjsTuning?.enableLowInitialPlaylist ?? true;
    vhsOptions.useBandwidthFromLocalStorage =
      vjsTuning?.useBandwidthFromLocalStorage ?? true;
    vhsOptions.limitRenditionByPlayerDimensions =
      vjsTuning?.limitRenditionByPlayerDimensions ?? true;
    vhsOptions.bandwidth = tuning?.defaultBandwidth ?? 1800000;
  }

  return opts;
}

export class VideoJsPlayerController implements PlayerController {
  private readonly options: ControllerOptions;
  castSender?: CastSender;
  private isAutoTrackSelection = true;
  private selectedVideoTrackId: string | null = null;

  private player: videojs.Player | null = null;
  private currentMediaState: CurrentMediaState | null = null;
  private clientSideAdvertisement: ClientSideAdvertisementVideoJS | undefined;
  private fairPlayLoader: FairPlayLoader | undefined;
  private adsInit = false;
  private isOnFullScreen = false;

  constructor(options: ControllerOptions) {
    this.options = options;
    this.fairPlayLoader = new FairPlayLoader();

    this.setupCastSender();
  }

  initialize(): void {
    const { videoElement, imaContainer, onReady, withClientSideAds } =
      this.options;

    const playerOptions = buildPlayerOptions();
    const player = videojs(videoElement, playerOptions, () => {
      console.log("Loaded video.js version", videojs.VERSION);

      if (withClientSideAds && !this.adsInit) {
        this.adsInit = true;

        loadIMALib()
          .then((ima) => {
            this.clientSideAdvertisement = new ClientSideAdvertisementVideoJS(
              ima,
              player,
              {
                imaContainerElement: imaContainer,
                videoElement,
              },
            );
          })
          .catch(() => {
            console.error("Unable to load Google IMA SDK");
          })
          .finally(() => {
            onReady();
          });
      } else {
        onReady();
      }
    });
    // Trigger custom error to catch player error for listeners that are
    // registered after onReady callback
    player.on("error", () => {
      this.clientSideAdvertisement?.onPlayerError();
      player.trigger("custom-error");
    });

    player.on("xhr-hooks-ready", () => {
      const source = this.currentMediaState?.source;

      if (source && source.headers) {
        const playerXhrRequestHook = (options: videojs.PlayerOptions) => {
          options.beforeSend = (xhr: XMLHttpRequest) => {
            for (const key in source.headers) {
              if (source.headers[key]) {
                xhr.setRequestHeader(key, source.headers[key]);
              }
            }
          };
          return options;
        };
        player.tech().vhs.xhr.onRequest(playerXhrRequestHook);
      }
    });

    const qualityLevels = player.qualityLevels();
    qualityLevels?.addEventListener("addqualitylevel", () => {
      this.applyLiveAutoTrackRestriction(player);
    });

    this.applyLiveAutoTrackRestriction(player);
    this.player = player;
  }

  async loadMedia(
    source: PlayerSource,
    autoplay?: boolean,
    startPosition?: number,
  ) {
    const isAppleDevice = videojs.browser.IS_SAFARI || videojs.browser.IS_IOS;
    const isFairPlay = isAppleDevice && isFairPlaySource(source);
    const sourceId = source.id;

    // Check if Encrypted Media Extensions are already initialized
    if (this.player.eme instanceof Function) {
      // Supports Encrypted Media Extensions for playback of encrypted content in video.js
      this.player.eme();
    }

    if (isAppleDevice) {
      // To play M3U8 stream with FairPlay on an Apple device / Safari, it needs enabled native support
      this.player.options_.html5.vhs.overrideNative = false;
    }

    if (isFairPlay) {
      const { certificateUri, licenseUri } =
        source.keySystems["com.apple.fps.1_0"];

      this.fairPlayLoader?.initialize(certificateUri, licenseUri);
      await this.fairPlayLoader?.loadCertificate();
    }

    if (sourceId && this.castSender?.isConnected()) {
      this.castSender?.load({
        source: `${sourceId}`,
        sessionToken: this.options.sessionToken,
        startTime: startPosition,
      });
    }

    this.currentMediaState = {
      source,
      autoplay,
      startPosition,
    };

    // eslint-disable-next-line unused-imports/no-unused-vars
    const { keySystems, ...rest } = source;

    // For FairPlay, we need to remove keySystems to enable custom loaders
    // that support AirPlay playback
    const finalSource = isFairPlay ? rest : source;

    this.player?.loadMedia({ src: finalSource }, () => {
      if (isFairPlay) {
        this.fairPlayLoader?.injectLoader(this.player?.tech().el());
      }

      if (source.adsTagUrl) {
        this.clientSideAdvertisement?.initialize(
          source.adsTagUrl,
          // Apple does not allow to start ads with sound, it will block ads
          // playback and in result ad will be skipped by SDK, we need to mute
          // ad before play to correctly show ads on iPad
          this.options.isAppleDevice,
        );
      } else {
        this.player?.trigger("nopreroll");
        this.player?.trigger("nopostroll");
      }

      if (source.subtitles) {
        for (const subtitle of source.subtitles) {
          if (subtitle.ContentType === "text/vtt") {
            this.player?.addRemoteTextTrack({
              kind: "subtitles",
              mode: "disabled",
              label: subtitle.Label,
              language: subtitle.LanguageCode,
              id: subtitle.StreamId,
              src: subtitle.ContentUrl,
            });
          }
        }
      }

      if (startPosition) {
        this.player?.currentTime(startPosition);
      }

      if (autoplay) {
        this.player?.play();
      }
    });
  }

  dispose(): void {
    this.clientSideAdvertisement?.dispose();
    this.player?.dispose();
    this.castSender?.destroy();
  }

  pause(): void {
    if (this.castSender?.isConnected()) {
      this.castSender?.pause();
      return;
    }

    this.player?.pause();
  }

  play(): void {
    if (this.castSender?.isConnected()) {
      this.castSender?.play();
      return;
    }

    this.player?.play();
  }

  resume(startPosition: number): void {
    this.player?.currentTime(startPosition);
    this.player?.play();

    const sourceId = this.currentMediaState?.source.id;
    if (sourceId && this.castSender?.isConnected()) {
      this.castSender?.load({
        source: `${sourceId}`,
        sessionToken: this.options.sessionToken,
        startTime: startPosition,
      });
    }
  }

  isPaused(): boolean {
    if (this.castSender?.isConnected()) {
      return !this.castSender?.isPlaying();
    }

    return Boolean(this.player?.paused());
  }

  isLive(): boolean {
    return Boolean(
      this.player?.liveTracker && this.player.liveTracker.isLive(),
    );
  }

  liveSeekableStart(): number {
    return this.player?.liveTracker.seekableStart() || 0;
  }

  liveSeekableEnd(): number {
    return this.player?.liveTracker.seekableEnd() || 0;
  }

  liveWindow(): number {
    return this.player?.liveTracker.liveWindow() || 0;
  }

  atLiveEdge(): boolean {
    return Boolean(this.player?.liveTracker.atLiveEdge());
  }

  seekToLiveEdge(): void {
    this.isLive() && this.player?.liveTracker.seekToLiveEdge();
  }

  liveCurrentTime(): number {
    return this.player?.liveTracker.liveCurrentTime() || 0;
  }

  currentTime(position?: number): number {
    if (position !== undefined) {
      if (this.castSender?.isConnected()) {
        this.castSender?.seekTo(position);
      } else {
        this.player?.currentTime(position);
      }
    }

    if (this.castSender?.isConnected()) {
      return this.castSender?.getCurrentTime() || this.player?.currentTime();
    }

    return this.player?.currentTime() || 0;
  }

  seekForward(seconds: number) {
    const currentTime = this.currentTime();
    const duration = this.isLive() ? this.liveSeekableEnd() : this.duration();

    this.currentTime(
      currentTime + seconds <= duration ? currentTime + seconds : duration,
    );
  }

  seekBackward(seconds: number) {
    const currentTime = this.currentTime();
    const seekableStart = this.liveSeekableStart();

    let newTime: number;

    switch (true) {
      case seekableStart && currentTime - seconds <= seekableStart:
        newTime = seekableStart;
        break;
      case currentTime >= seconds:
        newTime = currentTime - seconds;
        break;
      default:
        newTime = 0;
    }

    this.currentTime(newTime);
  }

  duration(): number {
    return this.player?.duration() || 0;
  }

  muted(state?: boolean): boolean {
    if (state !== undefined) {
      if (this.isCastConnected()) {
        this.castSender?.setMuted(state);
      } else {
        this.player?.muted(state);
        this.clientSideAdvertisement?.muted(state);
      }
    }

    if (this.isCastConnected()) {
      return this.castSender?.isMuted() || false;
    }

    return Boolean(this.player?.muted());
  }

  volume(value?: number): number {
    if (value !== undefined) {
      if (this.isCastConnected()) {
        this.castSender?.setVolume(value);
      } else {
        this.player?.volume(value);
        this.clientSideAdvertisement?.volume(value);
      }
    }

    if (this.isCastConnected()) {
      return this.castSender?.getVolume() || 0;
    }

    return this.player?.volume() || 0;
  }

  playbackRate(value?: number) {
    if (value !== undefined) {
      this.player?.playbackRate(value);
    }

    return (this.player?.playbackRate() || 1) as number;
  }

  subtitleTracks(): PlayerSubtitleTrack[] {
    const tracks: TextTrackList = this.player?.textTracks() || [];

    return Array.from(tracks)
      .filter((track) => ["subtitles"].includes(track.kind))
      .map((track) => {
        return {
          id: track.id,
          label: track.label,
          active: track.mode === "showing",
          language: track.language,
        };
      });
  }

  audioTracks(): PlayerAudioTrack[] {
    const tracks: videojs.AudioTrackList = this.player?.audioTracks() || [];

    return Array.from(tracks).map((track: VideojsAudioTrack) => {
      return {
        id: track.id,
        label: track.label,
        active: track.enabled,
        language: track.language,
      };
    });
  }

  videoTracks(): PlayerVideoTrack[] {
    const tracks: QualityLevelList = this.player?.qualityLevels();

    if (!tracks || tracks.length === 0) {
      return [];
    }

    const availableTracks = Array.from(tracks)
      .filter((track) => {
        const { width = 0, height = 0 } = track;
        const pixels = width > height ? height : width;

        return pixels > 0;
      })
      .map((track, index) => {
        const { id, width = 0, height = 0, bitrate, frameRate } = track;

        const pixels = width > height ? height : width;

        return {
          id,
          width,
          height,
          bitrate,
          label: `${pixels}p`,
          active: tracks.selectedIndex === index,
          framerate: frameRate,
        };
      })
      .sort((a, b) => (a.height > b.height ? -1 : 1));

    const tracksByLabel = groupBy(availableTracks, (item) => item.label);

    // Return only single track per label when multiple bitrate or framerate are available per label
    return Object.entries(tracksByLabel).map(([, tracks]) =>
      tracks.find(Boolean),
    ) as PlayerVideoTrack[];
  }

  onAudioTrackChange(callback: () => void): () => void {
    const tracks = this.player?.audioTracks();

    tracks?.addEventListener("addtrack", callback);
    tracks?.addEventListener("removetrack", callback);
    tracks?.addEventListener("labelchange", callback);
    tracks?.addEventListener("change", callback);

    return () => {
      tracks?.removeEventListener("addtrack", callback);
      tracks?.removeEventListener("removetrack", callback);
      tracks?.removeEventListener("labelchange", callback);
      tracks?.removeEventListener("change", callback);
    };
  }

  onSubtitleTrackChange(callback: () => void): () => void {
    const tracks = this.player?.textTracks();

    tracks?.addEventListener("addtrack", callback);
    tracks?.addEventListener("removetrack", callback);
    tracks?.addEventListener("labelchange", callback);
    tracks?.addEventListener("change", callback);
    tracks?.addEventListener("selectedlanguagechange", callback);

    return () => {
      tracks?.removeEventListener("addtrack", callback);
      tracks?.removeEventListener("removetrack", callback);
      tracks?.removeEventListener("labelchange", callback);
      tracks?.removeEventListener("change", callback);
      tracks?.removeEventListener("selectedlanguagechange", callback);
    };
  }

  onVideoTrackChange(callback: () => void): () => void {
    const tracks = this.player?.qualityLevels();

    tracks?.addEventListener("addqualitylevel", callback);
    tracks?.addEventListener("change", callback);

    return () => {
      tracks?.removeEventListener("addqualitylevel", callback);
      tracks?.removeEventListener("change", callback);
    };
  }

  onEvent(
    event: PlayerEvent | PlayerEvent[],
    callback: () => void,
  ): () => void {
    this.player?.on(event, callback);

    return () => {
      this.player?.off(event, callback);
    };
  }

  setVideoTrack(id: string | "auto") {
    const autoSelect = id === "auto";
    const tracks = this.player?.qualityLevels();
    this.isAutoTrackSelection = autoSelect;
    this.selectedVideoTrackId = autoSelect ? null : id;

    if (!tracks || tracks.length === 0) {
      return;
    }

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      track.enabled = autoSelect ? true : track.id === id;
    }

    this.applyLiveAutoTrackRestriction();
  }

  setAudioTrack(id: string) {
    const tracks = this.player?.audioTracks();

    if (!tracks || tracks.length === 0) {
      return;
    }

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      track.enabled = track.id === id;
    }
  }

  setSubtitleTrack(id: string | "disabled") {
    const disable = id === "disabled";
    const tracks = this.player?.textTracks();

    if (!tracks || tracks.length === 0) {
      return;
    }

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      if (track.kind === "subtitles") {
        if (disable && track.mode === "showing") {
          track.mode = "hidden";
        } else {
          track.mode = track.id === id ? "showing" : "disabled";
        }
      }
    }
  }

  setError(message: string, messageKey?: string) {
    this.player.trigger({
      type: "custom-error",
      message: message,
      messageKey: messageKey,
    });
  }

  error() {
    const error = this.player?.error() as videojs.MediaError;

    return error
      ? { type: getErrorMessageByStatusCode(error.code.toString()) }
      : null;
  }

  reload() {
    const { videoElement, onReload } = this.options;

    if (this.currentMediaState && videoElement) {
      const { source, startPosition, autoplay } = this.currentMediaState;

      onReload();
      this.initialize();
      this.clientSideAdvertisement?.onPlayerReload();
      this.loadMedia(source, autoplay, startPosition);
    }
  }

  adCuePoints() {
    return this.clientSideAdvertisement?.cuePoints || [];
  }

  requestNativeFullScreen() {
    // invalid type in native video.js lib

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.player?.requestFullscreen() as Promise<void>;
  }

  exitNativeFullScreen() {
    // invalid type in native video.js lib

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.player?.exitFullscreen() as Promise<void>;
  }

  isNativeFullScreen() {
    return this.player?.isFullscreen() || false;
  }

  toggleNativeFullScreen() {
    return this.isNativeFullScreen()
      ? this.exitNativeFullScreen()
      : this.requestNativeFullScreen();
  }

  currentAdTracker() {
    return this.clientSideAdvertisement?.tracker;
  }

  pauseAd(): void {
    this.clientSideAdvertisement?.pause();
  }

  resumeAd(): void {
    this.clientSideAdvertisement?.resume();
  }

  skipAd(): void {
    this.clientSideAdvertisement?.skip();
  }

  private setupCastSender() {
    if (this.castSender) {
      return;
    }

    if (!getPlayerRuntimeConfig().chromecastReceiverId) {
      return;
    }

    this.castSender = new CastSender({
      onConnected: () => {
        const sourceId = this.currentMediaState?.source.id;
        if (sourceId) {
          this.castSender?.load({
            source: `${sourceId}`,
            sessionToken: this.options.sessionToken,
            startTime: this.player?.currentTime(),
          });
        }
        this.player?.pause();
        this.player?.trigger("castconnected");
        this.player?.trigger("volumechange");
      },
      onDisconnected: () => {
        const latestTimeFromRemote = this.castSender?.getCurrentTime();
        if (latestTimeFromRemote) {
          this.player?.currentTime(latestTimeFromRemote);
        }
        this.player?.trigger("castdisconnected");
        this.player?.trigger("volumechange");
        setTimeout(() => {
          this.player?.play();
        }, 1000);
      },
      onStateChanged: (
        newState: ChromecastPlayerState,
        oldState: ChromecastPlayerState,
      ) => {
        if (!this.isCastConnected()) {
          return;
        }
        if (newState.playbackState != oldState.playbackState) {
          switch (newState.playbackState) {
            case PlaybackState.PLAYING:
              this.player?.trigger("playing");
              break;
            case PlaybackState.PAUSED:
              this.player?.trigger("pause");
              break;
            case PlaybackState.ENDED:
              this.player?.trigger("ended");
              break;
          }
        }
        if (newState.currentTime != oldState.currentTime) {
          this.player?.trigger("timeupdate");
        }
      },
      onVolumeLevelChange: () => {
        this.player?.trigger("volumechange");
      },
      onMuteChanged: () => {
        this.player?.trigger("volumechange");
      },
      onError: (error) => {
        this.player.trigger("custom-error", error);
      },
    });
  }

  isCastConnected(): boolean {
    return Boolean(this.castSender?.isConnected());
  }

  getCastDeviceName(): string {
    if (this.castSender?.isConnected()) {
      return this.castSender?.getDeviceName();
    }

    return "";
  }

  toggleAirPlay() {
    const videoElement = this.player.el().querySelector("video");
    videoElement?.webkitShowPlaybackTargetPicker?.();
  }

  onAirPlayAvailabilityChange(callback: (e: AirPlayAvailability) => void) {
    const videoElement = this.player.el().querySelector("video");
    const handleAvailabilityChange = (e: AirPlayAvailabilityChangeEvent) => {
      callback(e.availability);
    };

    videoElement.addEventListener(
      AirPlayEvent.AVAILABILITY_CHANGED,
      handleAvailabilityChange,
    );
    return () => {
      videoElement.removeEventListener(
        AirPlayEvent.AVAILABILITY_CHANGED,
        handleAvailabilityChange,
      );
    };
  }

  onAirPlayStatusChange(callback: (isCasting: boolean) => void) {
    const videoElement = this.player.el().querySelector("video");
    const handleTargetChange = () => {
      const isCasting = videoElement?.webkitCurrentPlaybackTargetIsWireless;
      callback(isCasting);
    };

    videoElement.addEventListener(
      AirPlayEvent.TARGET_CHANGED,
      handleTargetChange,
    );
    return () => {
      videoElement.removeEventListener(
        AirPlayEvent.TARGET_CHANGED,
        handleTargetChange,
      );
    };
  }

  onEnterFullScreen(): void {
    if (!this.isOnFullScreen) {
      this.isOnFullScreen = true;
      this.clientSideAdvertisement?.onEnterFullScreen();
    }
  }

  onLeaveFullScreen(): void {
    if (this.isOnFullScreen) {
      this.isOnFullScreen = false;
      this.clientSideAdvertisement?.onLeaveFullScreen();
    }
  }

  private applyLiveAutoTrackRestriction(player = this.player) {
    if (!player) {
      return;
    }

    const tracks = player.qualityLevels();

    if (!tracks || tracks.length === 0) {
      return;
    }

    if (!this.isAutoTrackSelection && this.selectedVideoTrackId) {
      let hasSelectedTrack = false;
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].id === this.selectedVideoTrackId) {
          hasSelectedTrack = true;
          break;
        }
      }

      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        track.enabled = hasSelectedTrack
          ? track.id === this.selectedVideoTrackId
          : true;
      }
      return;
    }

    const config = getPlayerRuntimeConfig();
    const liveTuningEnabled = config.liveTuning?.enabled ?? false;
    const liveMaxHeight = config.liveTuning?.maxHeight ?? 0;

    if (!liveTuningEnabled || !liveMaxHeight || liveMaxHeight <= 0) {
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].enabled = true;
      }
      return;
    }

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const height = Math.min(track.width || 0, track.height || 0);

      if (!height) {
        continue;
      }

      track.enabled = height <= liveMaxHeight;
    }
  }
}
